import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import EditableEmail from '../components/EditableEmail';
import { submissionsAPI, clientsAPI, type WebhookSubmission } from '../services/api';
import { Button, Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { FileText, Send, X, Search, Filter, BarChart3, ScrollText, Trash2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Download } from 'lucide-react';
import { formatLocalDateTime } from '../utils/timezone';
import { safeHttpUrl } from '../utils/safeUrl';
import api from '../services/api';

type PeriodType = 'week' | 'month' | '3months' | '6months' | 'year';

interface SubmissionStats {
  period: string;
  start_date: string;
  end_date: string;
  total_count: number;
  manager_stats: Array<{
    manager_id: number;
    manager_name: string;
    manager_email: string;
    count: number;
  }>;
  unassigned_count: number;
}

type SortDirection = 'asc' | 'desc';

interface Manager {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

const SubmissionsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialPageSize = parseInt(searchParams.get('page_size') || '25', 10);
  const initialSearch = searchParams.get('search') || '';
  const initialStatusFilter = searchParams.get('status') || '';
  const initialSourceFilter = searchParams.get('source') || '';
  const initialManagerFilter = searchParams.get('manager') || '';
  
  const [submissions, setSubmissions] = useState<WebhookSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState<WebhookSubmission | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedJson, setEditedJson] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState(initialSearch); // Input value (updates immediately)
  const [searchTerm, setSearchTerm] = useState(initialSearch); // Actual search term (updates with debounce)
  const [statusFilter, setStatusFilter] = useState<string>(initialStatusFilter);
  const [sourceFilter, setSourceFilter] = useState<string>(initialSourceFilter);
  const [managerFilter, setManagerFilter] = useState<string>(initialManagerFilter);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc'); // Default: newest first
  const [managers, setManagers] = useState<Manager[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [stats, setStats] = useState<{ [key: string]: SubmissionStats }>({});
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('week');
  const [loadingStats, setLoadingStats] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState<{
    logs: Array<{ timestamp: string; level: string; message: string; details?: any }>;
    summary: any;
    submission_id: number;
    email: string | null;
  } | null>(null);
  const [rawLogs, setRawLogs] = useState<string>('');
  const [logsFormat, setLogsFormat] = useState<'json' | 'raw'>('raw');
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState<string>('');
  const [exportEndDate, setExportEndDate] = useState<string>('');
  const [exportFiles, setExportFiles] = useState<{
    csv?: { file_id: string; filename: string; download_url: string };
    excel?: { file_id: string; filename: string; download_url: string };
  }>({});
  const [exportGenerating, setExportGenerating] = useState(false);
  const [exportDownloading, setExportDownloading] = useState<'csv' | 'excel' | null>(null);

  // Update URL when state changes
  const updateURL = useCallback((updates: { 
    page?: number; 
    page_size?: number; 
    search?: string;
    status?: string;
    source?: string;
    manager?: string;
  }) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (updates.page !== undefined) {
      if (updates.page === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', updates.page.toString());
      }
    }
    
    if (updates.page_size !== undefined) {
      if (updates.page_size === 25) {
        newParams.delete('page_size');
      } else {
        newParams.set('page_size', updates.page_size.toString());
      }
    }
    
    if (updates.search !== undefined) {
      if (updates.search === '') {
        newParams.delete('search');
      } else {
        newParams.set('search', updates.search);
      }
    }
    
    if (updates.status !== undefined) {
      if (updates.status === '') {
        newParams.delete('status');
      } else {
        newParams.set('status', updates.status);
      }
    }
    
    if (updates.source !== undefined) {
      if (updates.source === '') {
        newParams.delete('source');
      } else {
        newParams.set('source', updates.source);
      }
    }
    
    if (updates.manager !== undefined) {
      if (updates.manager === '') {
        newParams.delete('manager');
      } else {
        newParams.set('manager', updates.manager);
      }
    }
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load managers for filter dropdown
  const loadManagers = useCallback(async () => {
    try {
      const response = await api.get('/api/users/');
      let allUsers: any[] = [];
      if (Array.isArray(response.data)) {
        allUsers = response.data;
      } else if (response.data && typeof response.data === 'object' && 'results' in response.data) {
        allUsers = (response.data as any).results || [];
      }
      const managersList = allUsers
        .filter((u: any) => u.role === 'manager')
        .map((u: any) => ({
          id: u.id,
          email: u.email,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
        }));
      setManagers(managersList);
    } catch (err: any) {
      console.error('Failed to load managers:', err);
    }
  }, []);

  // Load unique sources from submissions
  const loadSources = useCallback(async () => {
    try {
      // Use optimized endpoint to get unique sources
      const response = await api.get('/api/submissions/sources/');
      if (response && response.data && response.data.sources) {
        setSources(response.data.sources);
      }
    } catch (err: any) {
      console.error('Failed to load sources:', err);
      setSources([]);
    }
  }, []);

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };
      
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (managerFilter) params.manager = managerFilter;
      
      // Add ordering (default: newest first)
      params.ordering = sortDirection === 'asc' ? 'created_at' : '-created_at';
      
      const response = await submissionsAPI.getAll(params);
      
      if (response && typeof response === 'object') {
        if (Array.isArray(response.results)) {
          setSubmissions(response.results);
          setTotalCount(response.count || 0);
        } else if (Array.isArray(response)) {
          setSubmissions(response);
          setTotalCount(response.length);
        } else {
          setSubmissions([]);
          setTotalCount(0);
        }
      } else {
        setSubmissions([]);
        setTotalCount(0);
      }
    } catch (err: any) {
      console.error('Failed to load submissions:', err);
      setSubmissions([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, sourceFilter, managerFilter, sortDirection]);

  const handleEmailUpdate = useCallback(async (oldEmail: string, newEmail: string) => {
    await clientsAPI.updateEmailByEmail(oldEmail, newEmail);
    // Reload submissions to reflect the change
    loadSubmissions();
  }, [loadSubmissions]);

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
    updateURL({ page: 1, status: value });
  };

  const handleSourceFilterChange = (value: string) => {
    setSourceFilter(value);
    setCurrentPage(1);
    updateURL({ page: 1, source: value });
  };

  const handleManagerFilterChange = (value: string) => {
    setManagerFilter(value);
    setCurrentPage(1);
    updateURL({ page: 1, manager: value });
  };

  const handleSort = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    setSortDirection(newDirection);
    setCurrentPage(1);
    // Note: ordering is handled in loadSubmissions, no need to update URL
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    const newPage = 1;
    setCurrentPage(newPage);
    updateURL({ page: newPage, page_size: newSize });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    updateURL({ page: newPage });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const PaginationComponent = () => {
    const getPageNumbers = () => {
      const pages: (number | string)[] = [];
      const maxVisible = 7;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (currentPage > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (currentPage < totalPages - 2) {
          pages.push('...');
        }
        
        pages.push(totalPages);
      }
      
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-700">
            {totalCount > 0 ? (
              <>Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} submissions</>
            ) : (
              <>No submissions found</>
            )}
          </div>
          {totalCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none bg-white"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-1">
              {getPageNumbers().map((page, index) => {
                if (page === '...') {
                  return (
                    <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                      ...
                    </span>
                  );
                }
                
                const pageNum = page as number;
                const isActive = pageNum === currentPage;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      isActive
                        ? 'bg-accent-500 text-white font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const loadStats = useCallback(async (period: PeriodType) => {
    try {
      setLoadingStats(true);
      const data = await submissionsAPI.getStats(period);
      setStats(prev => ({ ...prev, [period]: data }));
    } catch (err: any) {
      console.error('Failed to load submission stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // Sync state with URL params when they change externally (e.g., browser back/forward or direct URL)
  // Use a ref to prevent multiple updates
  const isInitialMount = React.useRef(true);
  
  useEffect(() => {
    // Skip on initial mount to avoid duplicate requests
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    const urlPageSize = parseInt(searchParams.get('page_size') || '25', 10);
    const urlSearch = searchParams.get('search') || '';
    const urlStatus = searchParams.get('status') || '';
    const urlSource = searchParams.get('source') || '';
    const urlManager = searchParams.get('manager') || '';
    
    if (urlPage !== currentPage && urlPage >= 1) {
      setCurrentPage(urlPage);
    }
    if (urlPageSize !== pageSize && [25, 50, 100].includes(urlPageSize)) {
      setPageSize(urlPageSize);
    }
    if (urlSearch !== searchTerm) {
      setSearchTerm(urlSearch);
      setSearchInput(urlSearch);
    }
    if (urlStatus !== statusFilter) {
      setStatusFilter(urlStatus);
    }
    if (urlSource !== sourceFilter) {
      setSourceFilter(urlSource);
    }
    if (urlManager !== managerFilter) {
      setManagerFilter(urlManager);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Debounce search term - update after user stops typing for 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        const newPage = 1;
        setCurrentPage(newPage);
        updateURL({ page: newPage, search: searchInput });
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load submissions when dependencies change
  useEffect(() => {
    if (user && user.can_view_all_tasks) {
      loadSubmissions();
    }
  }, [user, loadSubmissions]);

  // Track if initial load is done to prevent duplicate requests
  const initialLoadDone = React.useRef(false);
  
  // Load managers and sources on mount (only once)
  useEffect(() => {
    if (user && user.can_view_all_tasks && !initialLoadDone.current) {
      initialLoadDone.current = true;
      loadManagers();
      loadSources();
      // Load stats only for selected period initially (lazy load others when needed)
      loadStats(selectedPeriod);
    }
  }, [user, loadManagers, loadSources, selectedPeriod, loadStats]);
  
  // Load stats when period changes (if not already loaded)
  useEffect(() => {
    if (user && user.can_view_all_tasks && selectedPeriod && !stats[selectedPeriod]) {
      loadStats(selectedPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedPeriod]); // Load stats only for selected period

  const handleViewJson = (submission: WebhookSubmission) => {
    setSelectedSubmission(submission);
    setEditedJson(JSON.stringify(submission.raw_json, null, 2));
    setIsModalOpen(true);
  };

  const handleSaveJson = async () => {
    if (!selectedSubmission) return;
    
    try {
      setSaving(true);
      const parsedJson = JSON.parse(editedJson);
      const updated = await submissionsAPI.updateJson(selectedSubmission.id, parsedJson);
      setSubmissions(submissions.map(s => s.id === updated.id ? updated : s));
      setSelectedSubmission(updated);
      setIsModalOpen(false);
    } catch (err: any) {
      console.error('Failed to update JSON:', err);
      alert('Invalid JSON or update failed');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to convert JSON logs to raw text format
  const formatLogsToText = (logs: Array<{ timestamp: string; level: string; message: string; details?: any }>): string => {
    if (!logs || logs.length === 0) {
      return 'No logs available';
    }

    return logs.map(log => {
      const timestamp = new Date(log.timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        fractionalSecondDigits: 3
      }).replace(',', '');
      
      const level = (log.level || 'info').toUpperCase().padEnd(5);
      const loggerName = log.details?.logger_name || log.details?.logger || 'system';
      let line = `${level} ${timestamp} ${loggerName} ${log.message}`;
      
      // Add details if present (excluding logger_name/logger to avoid duplication)
      if (log.details) {
        const detailsToShow = { ...log.details };
        delete detailsToShow.logger_name;
        delete detailsToShow.logger;
        
        const detailKeys = Object.keys(detailsToShow);
        if (detailKeys.length > 0) {
          line += ` ${JSON.stringify(detailsToShow)}`;
        }
      }
      
      return line;
    }).join('\n');
  };

  const handleViewLogs = async (submission: WebhookSubmission) => {
    try {
      setLoadingLogs(true);
      setLogsFormat('raw'); // Default to raw format
      
      // Load JSON format first (this works reliably)
      const jsonData = await submissionsAPI.getLogs(submission.id, 'json');
      
      // Handle response - check if it's wrapped in {status, data} or direct object
      let logsData = jsonData;
      if (jsonData && typeof jsonData === 'object' && 'data' in jsonData && 'status' in jsonData) {
        // Response is wrapped: {status: 200, data: {...}}
        logsData = (jsonData as any).data;
      }
      
      if (logsData && typeof logsData === 'object' && 'logs' in logsData) {
        setSelectedLogs(logsData);
        
        // Try to load raw format, but if it fails, generate from JSON
        let rawData = '';
        try {
          const rawResponse = await submissionsAPI.getLogs(submission.id, 'raw');
          rawData = typeof rawResponse === 'string' ? rawResponse : String(rawResponse || '');
          if (!rawData || rawData.trim() === '') {
            // If raw format is empty, generate from JSON
            rawData = formatLogsToText(logsData.logs);
          }
        } catch (rawError) {
          console.warn('Failed to load raw logs format, generating from JSON:', rawError);
          // Generate raw format from JSON data
          rawData = formatLogsToText(logsData.logs);
        }
        setRawLogs(rawData);
      } else {
        console.warn('Unexpected logs response format:', logsData);
        // Set empty logs if format is unexpected
        setSelectedLogs({
          logs: [],
          summary: { total: 0, by_level: {}, latest: null },
          submission_id: submission.id,
          email: submission.email,
        });
        setRawLogs('No logs available');
      }
      
      setLogsModalOpen(true);
    } catch (err: any) {
      console.error('Failed to load logs:', err);
      alert('Failed to load logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleResendToHubspot = async (submissionId: number) => {
    try {
      setResending(submissionId);
      const result = await submissionsAPI.resendToHubspot(submissionId);
      if (result.success) {
        await loadSubmissions();
        alert('Successfully resent to HubSpot!');
      } else {
        alert(result.error || 'Failed to resend to HubSpot');
      }
    } catch (err: any) {
      console.error('Failed to resend to HubSpot:', err);
      alert('Failed to resend to HubSpot');
    } finally {
      setResending(null);
    }
  };

  const handleDelete = async (submissionId: number) => {
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }
    
    try {
      await submissionsAPI.delete(submissionId);
      await loadSubmissions();
      alert('Submission deleted successfully');
    } catch (err: any) {
      console.error('Failed to delete submission:', err);
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'form_completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'not_sent':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    // Convert UTC to user's local timezone
    return formatLocalDateTime(dateString);
  };

  if (!user || !user.can_view_all_tasks) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-900">Access Denied</h2>
              <p className="mt-2 text-gray-600">You don't have permission to view this page.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <FileText className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Webhook Submissions</h1>
                  <p className="mt-1 text-sm text-gray-600">Track and manage all webhook submissions</p>
                </div>
              </div>
              <button
                onClick={() => {
                  // Set default dates: 1 week ago to today
                  const today = new Date();
                  const weekAgo = new Date(today);
                  weekAgo.setDate(today.getDate() - 7);
                  
                  setExportStartDate(weekAgo.toISOString().split('T')[0]);
                  setExportEndDate(today.toISOString().split('T')[0]);
                  setExportFiles({});
                  setExportModalOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors font-medium"
              >
                <Download className="w-5 h-5" />
                Download
              </button>
            </div>
          </div>

          {/* Statistics Section */}
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary-600" />
                <h2 className="text-lg font-semibold text-gray-900">Submission Statistics</h2>
              </div>
              <select
                value={selectedPeriod}
                onChange={(e) => {
                  const newPeriod = e.target.value as PeriodType;
                  setSelectedPeriod(newPeriod);
                  // Load stats for new period if not already loaded
                  if (!stats[newPeriod]) {
                    loadStats(newPeriod);
                  }
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-sm"
              >
                <option value="week">Last Week</option>
                <option value="month">Last Month</option>
                <option value="3months">Last 3 Months</option>
                <option value="6months">Last 6 Months</option>
                <option value="year">Last Year</option>
              </select>
            </div>
            
            {loadingStats && !stats[selectedPeriod] ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-accent-500"></div>
                <p className="mt-2 text-sm text-gray-600">Loading statistics...</p>
              </div>
            ) : stats[selectedPeriod] ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {stats[selectedPeriod].manager_stats.map((manager) => (
                    <div
                      key={manager.manager_id}
                      className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-4 border border-primary-200"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-primary-900">{manager.manager_name}</p>
                          <p className="text-xs text-primary-700 mt-0.5">{manager.manager_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary-900">{manager.count}</p>
                          <p className="text-xs text-primary-700">submissions</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {stats[selectedPeriod].unassigned_count > 0 && (
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Unassigned</p>
                          <p className="text-xs text-gray-700 mt-0.5">No manager</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">{stats[selectedPeriod].unassigned_count}</p>
                          <p className="text-xs text-gray-700">submissions</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Total Submissions</span>
                    <span className="text-2xl font-bold text-primary-600">{stats[selectedPeriod].total_count}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No statistics available</p>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by email or HubSpot Contact ID..."
                  value={searchInput}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 appearance-none bg-white"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="error">Error</option>
                  <option value="form_completed">Form Completed</option>
                  <option value="not_sent">Not Sent</option>
                </select>
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={sourceFilter}
                  onChange={(e) => handleSourceFilterChange(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 appearance-none bg-white"
                >
                  <option value="">All Sources</option>
                  {sources.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={managerFilter}
                  onChange={(e) => handleManagerFilterChange(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 appearance-none bg-white"
                >
                  <option value="">All Managers</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id.toString()}>
                      {manager.first_name} {manager.last_name} ({manager.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Pagination - Top */}
          {totalCount > 0 && <PaginationComponent />}

          {/* Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500"></div>
              <p className="mt-2 text-gray-600">Loading submissions...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-600">No submissions found</p>
            </div>
          ) : (
            <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <button
                          onClick={handleSort}
                          className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                        >
                          Date
                          {sortDirection === 'asc' ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" style={{ maxWidth: '250px' }}>Email</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Source</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Stage</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">HS Status</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">HS Contact ID</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Manager</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Meet URL</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Call ID</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {submissions.map((submission) => (
                      <tr key={submission.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(submission.created_at)}</td>
                        <td className="px-6 py-4 text-sm text-gray-900" style={{ maxWidth: '250px' }}>
                          {submission.email ? (
                            <EditableEmail
                              email={submission.email}
                              onUpdate={(newEmail) => handleEmailUpdate(submission.email!, newEmail)}
                            />
                          ) : (
                            <span className="text-gray-400">No email</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {submission.source_display || submission.source || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {submission.form_stage ?? '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full border ${getStatusColor(submission.hubspot_status)}`}>
                            {submission.hubspot_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{submission.hubspot_contact_id || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.assigned_manager_email ? (
                            <div className="flex flex-col">
                              <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary-50 text-primary-700 text-xs font-medium mb-1">
                                {submission.assigned_manager_name || 'Manager'}
                              </span>
                              <span className="text-xs text-gray-600">
                                {submission.assigned_manager_email}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.google_meet_id ? (
                            (() => {
                              const meetHref = safeHttpUrl(submission.google_meet_id);
                              const meetLabel = submission.google_meet_id.length > 40
                                ? `${submission.google_meet_id.substring(0, 40)}...`
                                : submission.google_meet_id;
                              return meetHref ? (
                                <a
                                  href={meetHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-accent-600 hover:text-accent-700 hover:underline truncate max-w-xs block"
                                  title={submission.google_meet_id}
                                >
                                  {meetLabel}
                                </a>
                              ) : (
                                <span
                                  className="text-gray-500 truncate max-w-xs block"
                                  title={submission.google_meet_id}
                                >
                                  {meetLabel}
                                </span>
                              );
                            })()
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {submission.book_call_id ? (
                            <a
                              href={`/tasks?task=${submission.book_call_id}`}
                              className="text-accent-600 hover:text-accent-700 hover:underline font-medium"
                            >
                              #{submission.book_call_id}
                            </a>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <Button
                              onPress={() => handleViewJson(submission)}
                              className="flex items-center gap-1 px-3 py-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                            >
                              <FileText className="w-4 h-4" />
                              View JSON
                            </Button>
                            <Button
                              onPress={() => handleViewLogs(submission)}
                              isDisabled={loadingLogs}
                              className="flex items-center gap-1 px-3 py-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ScrollText className="w-4 h-4" />
                              View Log
                            </Button>
                            <Button
                              onPress={() => handleResendToHubspot(submission.id)}
                              isDisabled={resending === submission.id}
                              className="flex items-center gap-1 px-3 py-1.5 text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Send className="w-4 h-4" />
                              {resending === submission.id ? 'Sending...' : 'Resend'}
                            </Button>
                            {user?.role === 'owner' && (
                              <Button
                                onPress={() => handleDelete(submission.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination - Bottom */}
              {totalCount > 0 && <PaginationComponent />}
            </div>
          )}
        </div>
      </div>

      {/* JSON View/Edit Modal using React Aria */}
      <ModalOverlay 
        isOpen={isModalOpen} 
        onOpenChange={setIsModalOpen}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <Modal className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <Dialog className="flex flex-col h-full">
            {({ close }) => (
              <>
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <Heading slot="title" className="text-xl font-semibold text-gray-900">
                    Submission #{selectedSubmission?.id} - JSON Data
                  </Heading>
                  <Button
                    onPress={close}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                <div className="flex-1 overflow-auto p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Raw JSON (editable)
                    </label>
                    <textarea
                      value={editedJson}
                      onChange={(e) => setEditedJson(e.target.value)}
                      className="w-full h-96 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                      spellCheck={false}
                    />
                  </div>

                  {selectedSubmission?.hubspot_error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                      <strong>HubSpot Error:</strong> {selectedSubmission.hubspot_error}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                  <Button
                    onPress={close}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </Button>
                  <Button
                    onPress={handleSaveJson}
                    isDisabled={saving}
                    className="px-4 py-2 bg-accent-500 text-white rounded-md hover:bg-accent-600 disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    onPress={() => handleResendToHubspot(selectedSubmission?.id || 0)}
                    isDisabled={resending === selectedSubmission?.id}
                    className="px-4 py-2 bg-accent-500 text-white rounded-md hover:bg-accent-600 disabled:opacity-50 transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {resending === selectedSubmission?.id ? 'Sending...' : 'Resend to HubSpot'}
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>

      {/* Logs Modal */}
      {logsModalOpen && (
      <ModalOverlay 
        isOpen={logsModalOpen} 
        onOpenChange={setLogsModalOpen}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <Modal className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
          <Dialog className="flex flex-col h-full min-h-0 outline-none">
            {({ close }) => (
              <>
                <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200">
                  <Heading slot="title" className="text-xl font-semibold text-gray-900">
                    Submission #{selectedLogs?.submission_id} - Processing Logs
                    {selectedLogs?.email && (
                      <span className="text-sm font-normal text-gray-500 ml-2">
                        ({selectedLogs.email})
                      </span>
                    )}
                  </Heading>
                  <Button
                    onPress={close}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-6">
                  {selectedLogs && (
                    <>
                      {/* Summary */}
                      <div className="mb-6 bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Summary</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <div className="text-2xl font-bold text-primary-600">{selectedLogs.summary.total}</div>
                            <div className="text-xs text-gray-600">Total Logs</div>
                          </div>
                          {Object.entries(selectedLogs.summary.by_level).map(([level, count]) => (
                            <div key={level}>
                              <div className="text-2xl font-bold text-primary-600">{count as number}</div>
                              <div className="text-xs text-gray-600 capitalize">{level}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Format Toggle */}
                      <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-700">Log Entries</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setLogsFormat('raw')}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                              logsFormat === 'raw'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Raw
                          </button>
                          <button
                            onClick={() => setLogsFormat('json')}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                              logsFormat === 'json'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            Structured
                          </button>
                        </div>
                      </div>

                      {/* Logs Display */}
                      {logsFormat === 'raw' ? (
                        <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-xs max-h-96 overflow-y-auto">
                          <pre className="whitespace-pre-wrap break-words">{rawLogs || 'No logs available'}</pre>
                        </div>
                      ) : (
                      <div className="space-y-2">
                          {selectedLogs && selectedLogs.logs.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            No logs available for this submission
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                              {selectedLogs?.logs.map((log, index) => (
                              <div
                                key={index}
                                className={`border-l-4 p-3 rounded-r bg-gray-50 ${
                                  log.level === 'error' ? 'border-red-500' :
                                  log.level === 'warning' ? 'border-yellow-500' :
                                  log.level === 'success' ? 'border-green-500' :
                                  'border-blue-500'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-1">
                                  <span className={`text-xs font-semibold capitalize ${
                                    log.level === 'error' ? 'text-red-700' :
                                    log.level === 'warning' ? 'text-yellow-700' :
                                    log.level === 'success' ? 'text-green-700' :
                                    'text-blue-700'
                                  }`}>
                                    {log.level}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatLocalDateTime(log.timestamp)}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-800">{log.message}</div>
                                {log.details && (
                                  <details className="mt-2">
                                    <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-800">
                                      Details
                                    </summary>
                                    <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                                      {JSON.stringify(log.details, null, 2)}
                                    </pre>
                                  </details>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex-shrink-0 flex justify-end gap-3 p-6 border-t border-gray-200 bg-white">
                  <Button
                    onPress={close}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    )}

    {/* Export Modal */}
    {exportModalOpen && (
      <ModalOverlay isOpen={exportModalOpen} onOpenChange={setExportModalOpen} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Modal className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          <Dialog className="p-6">
            <div className="flex items-center justify-between mb-6">
              <Heading slot="title" className="text-2xl font-bold text-gray-900">Export Submissions</Heading>
              <button
                onClick={() => setExportModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none"
                />
              </div>

              {!exportFiles.csv && !exportFiles.excel && (
                <button
                  onClick={async () => {
                    if (!exportStartDate || !exportEndDate) {
                      alert('Please select both start and end dates');
                      return;
                    }

                    try {
                      setExportGenerating(true);
                      
                      // Generate both files
                      const [csvResult, excelResult] = await Promise.all([
                        submissionsAPI.exportData(exportStartDate, exportEndDate, 'csv'),
                        submissionsAPI.exportData(exportStartDate, exportEndDate, 'excel'),
                      ]);

                      setExportFiles({
                        csv: csvResult,
                        excel: excelResult,
                      });
                    } catch (err: any) {
                      console.error('Failed to generate export files:', err);
                      alert('Failed to generate export files: ' + (err.response?.data?.error || err.message));
                    } finally {
                      setExportGenerating(false);
                    }
                  }}
                  disabled={exportGenerating || !exportStartDate || !exportEndDate}
                  className="w-full px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exportGenerating ? 'Generating...' : 'Generate Files'}
                </button>
              )}

              {(exportFiles.csv || exportFiles.excel) && (
                <div className="space-y-3 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700">Download Files:</p>
                  
                  {exportFiles.csv && (
                    <button
                      onClick={async () => {
                        try {
                          setExportDownloading('csv');
                          const blob = await submissionsAPI.downloadExport(exportFiles.csv!.file_id, exportFiles.csv!.filename);
                          
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = exportFiles.csv!.filename;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (err: any) {
                          console.error('Failed to download CSV:', err);
                          alert('Failed to download CSV: ' + (err.response?.data?.error || err.message));
                        } finally {
                          setExportDownloading(null);
                        }
                      }}
                      disabled={exportDownloading === 'csv'}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-5 h-5" />
                      {exportDownloading === 'csv' ? 'Downloading...' : 'Download CSV'}
                    </button>
                  )}

                  {exportFiles.excel && (
                    <button
                      onClick={async () => {
                        try {
                          setExportDownloading('excel');
                          const blob = await submissionsAPI.downloadExport(exportFiles.excel!.file_id, exportFiles.excel!.filename);
                          
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = exportFiles.excel!.filename;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (err: any) {
                          console.error('Failed to download Excel:', err);
                          alert('Failed to download Excel: ' + (err.response?.data?.error || err.message));
                        } finally {
                          setExportDownloading(null);
                        }
                      }}
                      disabled={exportDownloading === 'excel'}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-5 h-5" />
                      {exportDownloading === 'excel' ? 'Downloading...' : 'Download Excel'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    )}
    </div>
  );
};

export default SubmissionsPage;
